/**
 * apertura.ts — la casa del CSV de saldos de apertura.
 *
 * ▶ POR QUÉ ESTÁ ACÁ Y NO EN LA HERRAMIENTA
 *
 * Vivía dentro de `herramientas/apertura-cargar.mjs`, que era el único camino.
 * Con la pantalla de E6.3 hay un segundo, y dos lectores del mismo CSV son dos
 * lecturas que divergen: el día que una acepte un separador que la otra no,
 * el mismo archivo carga distinto según por dónde entró — y un saldo de
 * apertura equivocado **cuadra igual** y no se ve nunca más.
 *
 * Así que hay uno solo, y lo importan los dos. La herramienta es `.mjs` e
 * importa este `.ts` directamente: Node lo lee quitando los tipos.
 *
 * ▶ LOS IMPORTES SE LEEN COMO TEXTO Y NO SE TOCAN
 *
 * Ni para sumar, ni para comprobar que cuadran, ni para «normalizar». Se pasan
 * tal cual a la base, que los guarda en `numeric` y decide. Un CSV con
 * 1250000.10 tiene que llegar a Postgres siendo 1250000.10, y `parseFloat` es
 * exactamente donde eso deja de ser cierto. Es la misma regla de
 * `src/nucleo/dinero.ts`, del otro lado de la casa.
 *
 * ▶ Y ESTE ARCHIVO NO DECIDE NADA MÁS
 *
 * Ni si cuadra, ni si la fecha sirve, ni si la empresa ya tiene apertura. Todo
 * eso lo dice `control.cargar_apertura`. Si acá se comprobara de nuevo serían
 * dos listas, y la de acá se quedaría vieja el día que la regla cambie.
 */

export interface LineaDeApertura {
  cuenta: string;
  debe?: string;
  haber?: string;
  detalle?: string;
  clasificacion?: Record<string, string>;
}

/**
 * Por qué falló, como CLAVE y no como frase.
 *
 * La primera versión devolvía el texto en español. Está mal por la regla de
 * siempre: **todo el texto que lee una persona vive en el diccionario**, que
 * es bilingüe. Una frase que nace acá llega en español a una pantalla en
 * inglés, y no hay dónde traducirla porque nadie sabe que es texto.
 *
 * La línea de comandos sí escribe en español, y puede: es la que tiene un solo
 * idioma. Cada quien arma la frase con esta clave.
 */
export type MotivoDeRechazo =
  | 'sin_filas'      // no hay nada debajo de la cabecera
  | 'falta_cuenta'   // la cabecera no trae la columna `cuenta`
  | 'sin_importe'    // una fila sin debe ni haber
  | 'sin_lineas';    // había filas pero ninguna con cuenta

export type LecturaDeApertura =
  | { ok: true; lineas: LineaDeApertura[] }
  | { ok: false; motivo: MotivoDeRechazo; fila?: number; dato?: string };

/**
 * Las columnas que son de ESTE formato y no del catálogo.
 *
 * ▶▶ Acá había una lista de dimensiones —`customer`, `project`, `department`—
 * y era la TERCERA copia del catálogo, después de la de la plantilla y la del
 * disparador. No falló: **se quedó corta en silencio.**
 *
 * `modalidad` nació en la `0071` y `supplier` en la `0074`, y ninguna de las
 * dos llegó acá. El síntoma no era un error: la columna simplemente no se leía,
 * así que un saldo de apertura no podía decir de qué modalidad era ni a qué
 * proveedor se le debía. Y en las cuentas por pagar eso deja el saldo
 * **atrapado**: la apertura está exenta de traer la dimensión, pero el pago no,
 * así que una deuda de apertura sin proveedor no se puede cancelar nunca.
 *
 * La lista se da vuelta: este módulo sabe cuáles columnas son suyas, y **todas
 * las demás son del catálogo**. Quién decide cuáles existen se le pasa desde
 * fuera, que es quien tiene el catálogo delante.
 */
export const COLUMNAS_PROPIAS = ['cuenta', 'nombre', 'debe', 'haber', 'detalle'] as const;

/**
 * Un CSV sencillo, con comillas dobles.
 *
 * No se usa `split(',')` a secas: un detalle como «Caja, banco y otros» parte
 * la línea en el sitio equivocado y corre todas las columnas siguientes. El
 * síntoma sería un saldo cargado en la cuenta de al lado — visible solo para
 * quien conozca los saldos de memoria.
 */
export function partirCsv(linea: string): string[] {
  const campos: string[] = [];
  let actual = '';
  let entreComillas = false;

  for (let i = 0; i < linea.length; i++) {
    const c = linea[i];
    if (entreComillas) {
      if (c === '"' && linea[i + 1] === '"') { actual += '"'; i++; }
      else if (c === '"') entreComillas = false;
      else actual += c;
    } else if (c === '"') entreComillas = true;
    else if (c === ',') { campos.push(actual); actual = ''; }
    else actual += c;
  }
  campos.push(actual);
  return campos.map((x) => x.trim());
}

/**
 * Lee el CSV entero y devuelve las líneas tal como las espera
 * `control.cargar_apertura`.
 *
 * Devuelve el problema en vez de reventar: quien llama es a veces una pantalla,
 * y una pantalla tiene que poder decir en qué fila está el error.
 */
export function leerApertura(
  crudo: string,
  /**
   * Qué dimensiones declara el catálogo de ESTA empresa.
   *
   * Por omisión ninguna, y eso es deliberado: quien no la pase lee un archivo
   * sin clasificación, que es recuperable. Lo que NO puede pasar es que este
   * módulo tenga su propia lista — ya la tuvo, y se quedó corta dos veces sin
   * que nada fallara. Ver `COLUMNAS_PROPIAS`.
   */
  dimensiones: readonly string[] = [],
): LecturaDeApertura {
  // El BOM que pone Excel al guardar en UTF-8. Sin quitarlo, la primera
  // columna se llama «\uFEFFcuenta» y no se encuentra — y el mensaje sería
  // «falta la columna cuenta» sobre un archivo que la tiene a la vista.
  const texto = crudo.replace(/^\uFEFF/, '');
  const filas = texto.split(/\r?\n/).filter((l) => l.trim() !== '');

  if (filas.length < 2) {
    return { ok: false, motivo: 'sin_filas' };
  }

  const cabecera = partirCsv(filas[0] ?? '').map((h) => h.toLowerCase());
  const col = (nombre: string) => cabecera.indexOf(nombre);
  if (col('cuenta') < 0) {
    // `dato` lleva la cabecera que SÍ vino: sin ella el mensaje es «falta una
    // columna» y la persona mira un archivo que la tiene, escrita de otra
    // forma o partida por un separador que no es la coma.
    return { ok: false, motivo: 'falta_cuenta', dato: cabecera.join(', ') };
  }

  const lineas: LineaDeApertura[] = [];

  for (let i = 1; i < filas.length; i++) {
    const c = partirCsv(filas[i] ?? '');
    const dame = (nombre: string) => {
      const j = col(nombre);
      return j >= 0 ? (c[j] ?? '') : '';
    };

    const cuenta = dame('cuenta');
    if (cuenta === '') continue;

    const debe = dame('debe');
    const haber = dame('haber');
    if (debe === '' && haber === '') {
      return { ok: false, motivo: 'sin_importe', fila: i + 1, dato: cuenta };
    }

    // ▶ Sólo las dimensiones que el catálogo declara, y no «toda columna que no
    // conozco»: una columna sobrante que alguien dejara en su hoja —«notas»,
    // «revisado»— se convertiría en una dimensión inventada que la base acepta
    // sin chistar si es de las abiertas. Un dato de más que nadie pidió es tan
    // malo como uno de menos.
    const clasificacion: Record<string, string> = {};
    for (const d of dimensiones) {
      const v = dame(d);
      if (v !== '') clasificacion[d] = v;
    }

    const linea: LineaDeApertura = { cuenta };
    // Solo una de las dos, y como TEXTO. `debe: ''` no es lo mismo que no
    // mandarlo: la base lo leería como cero y la línea tendría las dos.
    if (debe !== '') linea.debe = debe;
    if (haber !== '') linea.haber = haber;
    const detalle = dame('detalle');
    if (detalle !== '') linea.detalle = detalle;
    if (Object.keys(clasificacion).length > 0) linea.clasificacion = clasificacion;

    lineas.push(linea);
  }

  if (lineas.length === 0) {
    return { ok: false, motivo: 'sin_lineas' };
  }

  return { ok: true, lineas };
}

/* ═══════════════════════════════════════════════════════════════════════════
   Y LA OTRA MITAD: ESCRIBIR LA PLANTILLA
   ═══════════════════════════════════════════════════════════════════════════

   ▶ POR QUÉ ESTÁ ACÁ, junto al lector y no en la herramienta.

   Es la misma razón que puso el lector acá y está escrita arriba: **dos
   escritores del mismo CSV son dos formatos que divergen.** El día que uno
   ponga una columna que el otro no, el archivo que descarga el cliente deja de
   ser el que la pantalla sabe leer — y el síntoma sería un rechazo al cargar,
   con el archivo que el propio producto entregó.

   Lo escribía sólo `herramientas/apertura-plantilla.mjs`, o sea que la
   plantilla existía únicamente como comando NUESTRO. Y la pantalla de apertura
   le decía al cliente, con estas palabras, que la descargara **del catálogo**:
   un sitio donde no había ninguna descarga. Es la forma exacta que este
   proyecto persigue desde que el hub la nombró: *un bloqueo con su explicación
   perfecta y sin ningún sitio donde resolverlo.*

   Ahora la escribe esta función y la usan los dos.

   ▶ NO SE ESCRIBE NINGÚN CERO, y es la decisión que más importa del archivo.

   Sería más cómodo entregarlo con `0.00` en cada fila: cargaría sin quejarse y
   metería ciento veintitrés líneas de nada en el asiento de apertura. **Un cero
   es una afirmación** —«esta cuenta arrancó en cero»— y no es lo mismo que no
   tener saldo. Las filas que no se usen se borran, y una fila con cuenta y sin
   importe se rechaza diciendo cuál es (`sin_importe`).
*/

/** Lo que la plantilla necesita saber de una cuenta. */
export interface CuentaParaPlantilla {
  readonly codigo: string;
  readonly nombre: string;
  readonly permiteMovimiento: boolean;
  readonly activa: boolean;
}

/**
 * La cabecera, que es el contrato con el lector de arriba.
 *
 * `nombre` viaja para la persona y el lector la ignora —lee por nombre de
 * columna y las que no conoce no las mira—, y eso es lo que hace que el archivo
 * se pueda llenar sin tener el catálogo al lado.
 */
export function cabeceraDePlantilla(
  dimensiones: readonly string[],
): string {
  return [...COLUMNAS_PROPIAS, ...dimensiones].map(campo).join(',');
}

/** Un campo de CSV, con las mismas reglas que `partirCsv`. */
function campo(valor: string): string {
  return /[",\n\r]/.test(valor) ? `"${valor.replaceAll('"', '""')}"` : valor;
}

/**
 * El CSV de apertura con las cuentas de ESTA empresa, listo para llenar.
 *
 * ▶ Las cuentas salen del catálogo de la empresa y NO de la plantilla modelo.
 * Es la diferencia entre una plantilla útil y una que miente: el contador pudo
 * renombrar la 4110 y colgar auxiliares bajo la 1102, y una sacada del modelo
 * le ofrecería códigos que su empresa no tiene y le escondería los que sí.
 *
 * ▶ Y el filtro —hoja y activa— vive acá y no en cada llamador, porque es la
 * misma regla: una cuenta de mayor no recibe movimiento y una desactivada
 * tampoco, así que ofrecerlas sería ofrecer filas que la base va a rechazar.
 */
export function armarPlantilla(
  cuentas: readonly CuentaParaPlantilla[],
  dimensiones: readonly string[] = [],
): string {
  // Tantas comas como columnas: la cabecera y las filas se arman del MISMO
  // arreglo, para que agregar una dimensión no pueda correr los importes una
  // columna a la izquierda.
  const vacias = Array.from(
    { length: COLUMNAS_PROPIAS.length - 2 + dimensiones.length }, () => '');
  const filas = cuentas
    .filter((c) => c.permiteMovimiento && c.activa)
    .map((c) => [campo(c.codigo), campo(c.nombre), ...vacias].join(','));

  // ▶ Con BOM y con CRLF, y las dos cosas por el mismo motivo: este archivo lo
  // abre Excel en Windows. Sin BOM, «Depreciación» sale «DepreciaciÃ³n» y el
  // archivo parece corrupto cuando el que interpreta mal es el lector. El
  // lector de arriba quita el BOM al leer, así que el viaje de ida y vuelta
  // cierra.
  const BOM = String.fromCharCode(0xfeff);
  const CRLF = String.fromCharCode(13, 10);
  return `${BOM}${[cabeceraDePlantilla(dimensiones), ...filas].join(CRLF)}${CRLF}`;
}

/** Cómo se llama el archivo que se descarga. */
export function nombreDePlantilla(codigoDeEmpresa: string): string {
  return `apertura-${codigoDeEmpresa}.csv`;
}
